import { useState, useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { GoMotoFunction } from '../../../API/GoMoto.types';
import { PaginationActions, PaginationDispatchTypes, PaginationProps, PaginationReturnProps, PaginationState } from './Pagination.types';

/**
 * Handles paginating a series of elements based on how many elements are desired on a page.
 *
 * When needing to avoid having a scrollbar you can pass in the container element,
 * all elements that occupy height in the container, and any elements that share height in the
 * space needing to be measured. Then with the callbackRef in the return object, attach it to the element
 * that will be measured to get the height of a row. When the row element is found, the logic in the hook
 * will be started and it will determine how many rows can display on screen without causing a scrollbar
 * to appear and then automatically paginate with those constraints.
 *
 * @param props - {@link PaginationProps} description.
 * @returns `object` - {@link PaginationReturnProps} description.
 */
export const usePagination: GoMotoFunction<PaginationProps, PaginationReturnProps> = ({
    totalItems = 0,
    initialPage = 0,
    initialPageSize = 0,
    containerElement,
    gridElement,
    additionalElementsInContainer = [],
    additionalElementsInGrid = [],
}) => {
    // #region Variables
    const [pageSize, setPageSize] = useState<number>(initialPageSize);
    const [shouldShow, setShouldShow] = useState<boolean>(false);
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const [pageRowRef, setPageRowRef] = useState<HTMLElement | null>(null);

    const foundPageRowElement = useRef<boolean>(false);
    const rowHeightRef = useRef<number | null>(null);
    const containerStyleRef = useRef<CSSStyleDeclaration['overflowY'] | null>(null);
    const rowGapRef = useRef<number>(0);
    const maxGridHeightRef = useRef<number | null>(null);

    const filteredAdditionalElementsInContainer: HTMLElement[] = additionalElementsInContainer.filter((element: HTMLElement | null) => element !== null);
    const totalPages: number = useMemo(() => Math.ceil(totalItems / pageSize), [pageSize, totalItems]);
    // #endregion

    // #region Page Reducer
    /**
     * Handles all the logic for setting which page displays and proceeding to the previous or next page.
     *
     * @param state - The previous state of the paginationState.
     * @param action - Updated values based on the dispatch type that will be used to update the state.
     */
    const pageReducer: GoMotoFunction<[PaginationState, PaginationActions], PaginationState> = (state, action) => {
        let newEndIndex: number;

        switch (action.type) {
            case PaginationDispatchTypes.SetPage:
                newEndIndex = pageSize * (action.payload + 1);

                if (action.payload > totalPages) {
                    return state;
                }

                return {
                    ...state,
                    currentPage: action.payload,
                    startIndex: pageSize * action.payload,
                    endIndex: newEndIndex > totalItems ? totalItems : newEndIndex,
                    previousEnabled: action.payload > 0,
                    nextEnabled: action.payload + 1 < totalPages,
                };
            case PaginationDispatchTypes.NextPage:
                const incrementPage: number = state.currentPage + 1;
                newEndIndex = pageSize * (incrementPage + 1);

                if (incrementPage >= totalPages) {
                    return state;
                }

                return {
                    ...state,
                    currentPage: incrementPage,
                    startIndex: pageSize * incrementPage,
                    endIndex: newEndIndex > totalItems ? totalItems : newEndIndex,
                    previousEnabled: incrementPage > 0,
                    nextEnabled: incrementPage + 1 < totalPages,
                };
            case PaginationDispatchTypes.PreviousPage:
                newEndIndex = pageSize * state.currentPage;
                const decrementPage: number = state.currentPage - 1;

                if (state.currentPage <= 0) {
                    return state;
                }

                return {
                    ...state,
                    currentPage: decrementPage,
                    startIndex: pageSize * decrementPage,
                    endIndex: newEndIndex > totalItems ? totalItems : newEndIndex,
                    previousEnabled: decrementPage > 0,
                    nextEnabled: decrementPage + 1 < totalPages,
                };
            default:
                return state;
        }
    };

    const [paginationState, dispatch] = useReducer(pageReducer, {
        currentPage: initialPage,
        totalPages: totalPages,
        startIndex: pageSize * initialPage,
        endIndex: pageSize * (initialPage + 1) > totalItems ? totalItems : pageSize * (initialPage + 1),
        previousEnabled: initialPage > 0,
        nextEnabled: initialPage + 1 < totalPages,
    });
    // #endregion

    // #region getELementHeight
    /**
     * Finds the height of an element, including its' margins and borders.
     *
     * @param element - The element that is going to be measured.
     */
    const getElementHeight: GoMotoFunction<[HTMLElement | null | undefined], number> = useCallback((element) => {
        if (element !== null && element !== undefined) {
            const height: number = element.getBoundingClientRect().height;
            const styles: CSSStyleDeclaration = window.getComputedStyle(element);
            const margin: number = parseFloat(styles['marginTop']) + parseFloat(styles['marginBottom']);

            return height + margin;
        }
        else {
            return 0;
        }
    }, []);
    // #endregion

    // #region getMaxRowsInGrid
    /**
     * A recursive function to determine the exact number of rows that can be displayed within the remaining height
     * on the screen.
     *
     * @param maxRows - The number of rows that being measured to see if they can display without a scrollbar on the screen.
     */
    const getMaxRowsInGrid: GoMotoFunction<[number?], void> = useCallback((maxRows = 0) => {
        const filteredAdditionalElementsInGrid: HTMLElement[] = additionalElementsInGrid.filter((element: HTMLElement | null) => element !== null);

        const additionalHeightInGrid: number = filteredAdditionalElementsInGrid.reduce((accumulator: number, element: HTMLElement) => {
            return accumulator + getElementHeight(element);
        }, 0);
        const rowHeight: number = rowHeightRef.current ? rowHeightRef.current : 0;
        const totalRowGap: number = rowGapRef.current * (maxRows - 1);
        const totalHeight: number = (rowHeight * maxRows) + additionalHeightInGrid + totalRowGap;

        /** The total row-gap height if one more than the current maxRows was added to the height of the page. */
        const nextTotalRowGap: number = rowGapRef.current * maxRows;
        /** The total height of the grid if one more than the current maxRows was added to the height of the page. */
        const nextTotalHeight: number = (rowHeight * (maxRows + 1)) + additionalHeightInGrid + nextTotalRowGap;

        if (maxGridHeightRef.current) {
            if (totalHeight < maxGridHeightRef.current && nextTotalHeight < maxGridHeightRef.current) {
                getMaxRowsInGrid(maxRows + 1);
            }
            else {
                setPageSize(maxRows * 3);
                dispatch({ type: PaginationDispatchTypes.SetPage,
                    payload: 0 });
                setShouldShow(true);
            }
        }
        else {
            setShouldShow(true);
        }
    }, [additionalElementsInGrid, getElementHeight]);
    // #endregion

    /**
     * When initialPage, initialPageSize, or totalItems props change, reset the page
     * to the initial page to avoid showing a page that has nothing on it.
     */
    useEffect(() => {
        dispatch({
            type: PaginationDispatchTypes.SetPage,
            payload: initialPage,
        });
    }, [initialPage, initialPageSize, totalItems]);

    /**
     * when we retrieve the ref to the element that will be used to measure the height of the
     * row, retrieve the height of everything else needed and call getMaxRowsInGrid to find
     * the number of rows that can show per page.
     */
    useEffect(() => {
        if (pageRowRef && rowHeightRef.current === null && maxGridHeightRef.current === null) {
            const additionalHeightInContainer: number = filteredAdditionalElementsInContainer.reduce((accumulator: number, element: HTMLElement) => {
                return accumulator + getElementHeight(element);
            }, 0);

            rowHeightRef.current = getElementHeight(pageRowRef);
            maxGridHeightRef.current = getElementHeight(containerElement) - additionalHeightInContainer;

            if (gridElement !== null) {
                /**
                 * We get the row-gap property from the grid. The value returned is a string representing the pixel height (i.e. '21.6px').
                 * We then slice off the last 2 characters of the string and attempt to parse the string to a float. Since the last 2 characters
                 * should always be `px`, it should always be parsed as a valid number.
                 */
                rowGapRef.current = parseFloat(window.getComputedStyle(gridElement)
                    .getPropertyValue('row-gap')
                    .slice(0, -2));
            }

            getMaxRowsInGrid(undefined);
        }
    }, [containerElement, filteredAdditionalElementsInContainer, getElementHeight, getMaxRowsInGrid, gridElement, pageRowRef]);

    /**
     * When we finish finding the max number of rows that can show per page without a scrollbar, set the
     * overflowY of the container from hidden to auto.
     */
    useEffect(() => {
        if (shouldShow && containerElement) {
            containerElement.style.overflowY = 'auto';
        }
    }, [containerElement, shouldShow]);

    /**
     * When the container ref is passed to the hook, set the overflowY to hidden so we don't show a scrollbar
     * and initialize the hook so we can find the right height once we get the ref to the row height element.
     */
    useEffect(() => {
        if (containerElement && containerStyleRef.current === null) {
            containerStyleRef.current = containerElement.style.overflowY;
            containerElement.style.overflowY = 'hidden';
            setIsInitialized(true);
        }
    }, [containerElement]);

    return {
        setPage: useCallback((page: number) => {
            dispatch({
                type: PaginationDispatchTypes.SetPage,
                payload: page,
            });
        }, [dispatch]),
        setNextPage: useCallback(() => {
            dispatch({
                type: PaginationDispatchTypes.NextPage,
            });
        }, [dispatch]),
        setPreviousPage: useCallback(() => {
            dispatch({
                type: PaginationDispatchTypes.PreviousPage,
            });
        }, [dispatch]),
        setPageSize: useCallback((pageSize: number, nextPage: number = 0) => {
            setPageSize(pageSize);
            dispatch({
                type: PaginationDispatchTypes.SetPage,
                payload: nextPage,
            });
        }, [setPageSize]),
        currentPage: paginationState.currentPage,
        totalPages: paginationState.totalPages,
        startIndex: paginationState.startIndex,
        endIndex: paginationState.endIndex,
        previousEnabled: paginationState.previousEnabled,
        nextEnabled: paginationState.nextEnabled,
        pageSize: pageSize,
        totalItems: totalItems,
        shouldShow: shouldShow,
        isInitialized: isInitialized,
        pageRowCallbackRef: (node: HTMLElement | null) => {
            if (node !== null && !foundPageRowElement.current) {
                foundPageRowElement.current = true;
                setPageRowRef(node);
            }
        },
    };
};